import React, {
    Component,
    ReactElement
} from 'react';
import {connect} from 'react-redux';
import shallowEqual from 'shallowequal';

import {__} from '~/shared/src/utilities/messageCatalog';
import User from '~/shared/src/model/User';

import {Button} from 'primereact/button';
import {
    InputSwitch,
    InputSwitchChangeEvent
} from 'primereact/inputswitch';
import {Panel} from 'primereact/panel';

import ContentButton, {
    ReferenceType as ContentButtonType
} from '~/frontend/src/components/base/shared/ContentButton';

import {StoreState} from '~/frontend/src/store';


export type ColumnConfigMenuSingleColumn = {
    field : string;
    header? : string;
    hidden : boolean;
    hidable : boolean;
};

export type ColumnConfigMenuColumnsGroup = {
    key? : string;
    label : string;
    showHeader? : boolean;
    columns : ColumnConfigMenuSingleColumn[]
};

export type ColumnConfigMenuColumn = ColumnConfigMenuSingleColumn | ColumnConfigMenuColumnsGroup;

type DefaultProps = {
    disabled? : boolean;
    noSort?:boolean;
};
const defaultProps : Required<DefaultProps> = {
    disabled: false,
    noSort:false
};

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    id : string;

    /**
     * An array of columnsGroup objects for all columns with their visibility and props.
     */
    columns : ColumnConfigMenuColumn[];

    /**
     * Callback to invoke if expand columns to header width button is pressed.
     */
    onExpandColumnsToHeaderWidth : Function;

    /**
     * Callback to invoke if expand columns to screen width button is pressed.
     */
    onExpandColumnsToScreenWidth : Function;

    /**
     * Callback to invoke on column visibility change.
     *
     * @param {ColumnConfigMenuColumn[]} columns
     */
    onChange? : ( columns : ColumnConfigMenuColumn[] ) => void;

    /**
     * The CSS class name(s) to add to the component.
     */
    className? : string;

    tabIndex? : number;
};

type StateProps = {

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

export type ComponentProps = DefaultProps & Props;
type AllProps = Required<DefaultProps> & Props & StateProps;

type State = {
    columns : ColumnConfigMenuColumn[];
};

/**
 * Menu for column selection and configuration
 */
class ColumnConfigMenu extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    contentButton? : ContentButtonType | null;

    constructor( props : AllProps ) {
        super( props );

        this.state = {
            columns: ( [] as ColumnConfigMenuColumn[] ).concat( props.columns )
        };

        // bind event handlers
        this.onApplyButtonClick = this.onApplyButtonClick.bind( this );
        this.onCancelButtonClick = this.onCancelButtonClick.bind( this );
        this.onContentButtonCollapse = this.onContentButtonCollapse.bind( this );
        this.onExpandColumnsToHeaderWidth = this.onExpandColumnsToHeaderWidth.bind( this );
        this.onExpandColumnsToScreenWidth = this.onExpandColumnsToScreenWidth.bind( this );
    }

    /**
     * REDUX stuff
     */
    shouldComponentUpdate( nextProps : Props ) {
        if ( nextProps.columns !== this.props.columns ) {
            this.setState( { columns: nextProps.columns } );
            return false;
        }

        return true;
    }

    /**
     * Handler for apply button click
     *
     * @private
     */
    onApplyButtonClick( event : React.MouseEvent ) {
        if ( !shallowEqual( this.props.columns, this.state.columns ) ) {
            this.props.onChange?.( this.state.columns );
        }
        this.contentButton?.collapse( event );
    }

    /**
     * Handler for cancel button click
     *
     * @private
     */
    onCancelButtonClick( event : React.MouseEvent ) {
        this.setState( { columns: ( [] as ColumnConfigMenuColumn[] ).concat( this.props.columns ) } );
        this.contentButton?.collapse( event );
    }

    onContentButtonCollapse( event : React.MouseEvent, isOutsideClicked : boolean ) {
        if ( isOutsideClicked ) {
            this.setState( { columns: ( [] as ColumnConfigMenuColumn[] ).concat( this.props.columns ) } );
        }

        return isOutsideClicked;
    }

    /**
     * Handler for expand columns to header width button.
     *
     * @private
     */
    onExpandColumnsToHeaderWidth() {
        this.props.onExpandColumnsToHeaderWidth();
    }

    /**
     * Handler for expand columns to screen width button.
     *
     * @private
     */
    onExpandColumnsToScreenWidth() {
        this.props.onExpandColumnsToScreenWidth();
    }

    /**
     * Handler for toggleAllColumnVisibility button click
     *
     * @private
     */
    onToggleAllColumnsButtonClick( groupKey? : string ) {
        const hasColumnGroups = ( this.state.columns?.[ 0 ] as unknown as ColumnConfigMenuColumnsGroup )?.columns !== undefined;

        if ( groupKey && hasColumnGroups ) {
            const columnGroups = ( [] as ColumnConfigMenuColumnsGroup[] ).concat( this.state.columns as ColumnConfigMenuColumnsGroup[] );

            for ( let i = 0; i < columnGroups.length; i++ ) {
                if ( ( columnGroups[ i ].key ?? String( i ) ) === groupKey ) {
                    columnGroups[ i ] = Object.assign( {}, columnGroups[ i ] );
                    columnGroups[ i ].columns = ( [] as ColumnConfigMenuSingleColumn[] ).concat( columnGroups[ i ].columns );

                    let allColumnsVisible = true;

                    for ( let k = 0; k < columnGroups[ i ].columns.length; k++ ) {
                        if ( columnGroups[ i ].columns[ k ].hidden && columnGroups[ i ].columns[ k ].hidable ) {
                            allColumnsVisible = false;
                            break;
                        }
                    }

                    for ( let k = 0; k < columnGroups[ i ].columns.length; k++ ) {
                        if ( allColumnsVisible && !columnGroups[ i ].columns[ k ].hidden && columnGroups[ i ].columns[ k ].hidable ) {
                            columnGroups[ i ].columns[ k ] = Object.assign( {}, columnGroups[ i ].columns[ k ] );
                            columnGroups[ i ].columns[ k ].hidden = true;
                        }
                        else if ( !allColumnsVisible && columnGroups[ i ].columns[ k ].hidden && columnGroups[ i ].columns[ k ].hidable ) {
                            columnGroups[ i ].columns[ k ] = Object.assign( {}, columnGroups[ i ].columns[ k ] );
                            columnGroups[ i ].columns[ k ].hidden = false;
                        }
                    }

                    this.setState( { columns: columnGroups } );

                    break;
                }
            }
        }
        else if ( !groupKey ) {
            const columns = ( [] as ColumnConfigMenuSingleColumn[] ).concat( this.state.columns as ColumnConfigMenuSingleColumn[] );

            let allColumnsVisible = true;

            for ( let k = 0; k < columns.length; k++ ) {
                if ( columns[ k ].hidden && columns[ k ].hidable ) {
                    allColumnsVisible = false;
                    break;
                }
            }

            for ( let k = 0; k < columns.length; k++ ) {
                if ( allColumnsVisible && !columns[ k ].hidden && columns[ k ].hidable ) {
                    columns[ k ] = Object.assign( {}, columns[ k ] );
                    columns[ k ].hidden = true;
                }
                else if ( !allColumnsVisible && columns[ k ].hidden && columns[ k ].hidable ) {
                    columns[ k ] = Object.assign( {}, columns[ k ] );
                    columns[ k ].hidden = false;
                }
            }

            this.setState( { columns } );
        }
    }

    /**
     * Handler for showFlag InputSwitch onChange event.
     *
     * @param {string} groupKey
     * @param {string} columnField
     * @param {InputSwitchChangeEvent} event
     *
     * @private
     */
    onColumnVisibilityChange( groupKey : string, columnField : string, event : InputSwitchChangeEvent ) {
        const checked         = event.value,
              hasColumnGroups = ( this.state.columns?.[ 0 ] as unknown as ColumnConfigMenuColumnsGroup )?.columns !== undefined;

        if ( hasColumnGroups ) {
            const columnGroups = ( [] as ColumnConfigMenuColumnsGroup[] ).concat( this.state.columns as ColumnConfigMenuColumnsGroup[] );

            let columnGroupCount = 0;

            for ( let i = 0; i < columnGroups.length; i++ ) {
                const currentGroupKey = columnGroups[ i ].key ?? String( columnGroupCount );

                if ( currentGroupKey === groupKey ) {
                    columnGroups[ i ] = Object.assign( {}, columnGroups[ i ] );
                    columnGroups[ i ].columns = ( [] as ColumnConfigMenuSingleColumn[] ).concat( columnGroups[ i ].columns );

                    for ( let k = 0; k < columnGroups[ i ].columns.length; k++ ) {
                        if ( columnGroups[ i ].columns[ k ].field === columnField ) {
                            columnGroups[ i ].columns[ k ] = Object.assign( {}, columnGroups[ i ].columns[ k ] );
                            columnGroups[ i ].columns[ k ].hidden = !checked;

                            this.setState( { columns: columnGroups } );

                            break;
                        }
                    }

                    break;
                }

                columnGroupCount++;
            }
        }
        else {
            const columns = ( [] as ColumnConfigMenuSingleColumn[] ).concat( this.state.columns as ColumnConfigMenuSingleColumn[] );

            for ( let i = 0; i < columns.length; i++ ) {
                if ( columns[ i ].field === columnField ) {
                    columns[ i ] = Object.assign( {}, columns[ i ] );
                    columns[ i ].hidden = !checked;

                    this.setState( { columns } );

                    break;
                }
            }
        }
    }

    renderColumnOptions() {
        const { uid } = this.props;

        return <div className="flex">
            <Button
                id={`${uid}_expandAllColumnsToHeaderWidth`}
                onClick={this.onExpandColumnsToHeaderWidth}

                label={__( 'YN.base.columnConfig.expandAllColumnsToHeaderWidth' )}
                className="p-button-outlined"
            />
            <Button
                id={`${uid}_expandAllColumnsToScreenWidth`}
                onClick={this.onExpandColumnsToScreenWidth}

                label={__( 'YN.base.columnConfig.expandAllColumnsToScreenWidth' )}
                className="p-button-outlined ml-2"
            />
        </div>;
    }

    /**
     * Render the column groups with InputSwitches to toggle the visibility for their columns.
     *
     * @return {JSX.Element}
     *
     * @private
     */
    renderColumnGroups() {
        const groups          = [],
              {
                  noSort,
                  uid
              }               = this.props,
              hasColumnGroups = ( this.state.columns?.[ 0 ] as unknown as ColumnConfigMenuColumnsGroup )?.columns !== undefined;

        if ( hasColumnGroups ) {
            const columnGroups = this.state.columns as unknown as ColumnConfigMenuColumnsGroup[];

            let columnGroupCount = 0;

            for ( const columnGroup of columnGroups ) {
                const groupKey                      = columnGroup.key ?? String( columnGroupCount ),
                      groupColumns : ReactElement[] = [];

                let groupHeader : ReactElement = <></>,
                    className                  = 'yn-selectiongroup'

                // group header
                if ( columnGroup.showHeader ?? true ) {
                    const columnsCount        = columnGroup.columns.reduce( ( count, column ) => {
                              if ( !column.hidden || column.hidable ) {
                                  count++;
                              }

                              return count;
                          }, 0 ),
                          visibleColumnsCount = columnGroup.columns.reduce( ( count, column ) => {
                              if ( !column.hidden ) {
                                  count++;
                              }

                              return count;
                          }, 0 );

                    groupHeader = (
                        <div key={`${uid}_${groupKey}_header`}
                             className="yn-selectiongroup-header">
                            {columnGroup.label} ({visibleColumnsCount}/{columnsCount})
                        </div>
                    );
                }

                // group columns
                if ( !noSort ) {
                    columnGroup.columns.sort( ( column1, column2 ) => {
                        const sortLabel1 = ( column1.header ?? column1.field ?? '' ).toLowerCase(),
                              sortLabel2 = ( column2.header ?? column2.field ?? '' ).toLowerCase();

                        if ( sortLabel1 > sortLabel2 ) {
                            return 1;
                        }
                        else if ( sortLabel1 < sortLabel2 ) {
                            return -1;
                        }
                        else {
                            return 0;
                        }
                    } );
                }
                for ( const column of columnGroup.columns ) {
                    if ( !column.hidden || column.hidable ) {
                        // colons must not be part of HTML element id as we can't query them
                        const key = `${uid}_${groupKey}_${column.field}`.replace( /:/g, '_' );

                        groupColumns.push(
                            <div key={key} className="yn-selectiongroup-column">
                                <InputSwitch
                                    inputId={`${key}_switch`}
                                    checked={!column.hidden}
                                    disabled={!column.hidable}
                                    onChange={this.onColumnVisibilityChange.bind( this, groupKey, column.field )}
                                />
                                <label htmlFor={`${key}_switch`}>
                                    {column.header ?? column.field}
                                </label>
                            </div>
                        );
                    }
                }

                // spacing between groups
                if ( columnGroupCount > 0 ) {
                    className += ' yn-selectiongroup-spacer';
                }

                groups.push(
                    <div key={`${uid}_${groupKey}`} className={className}>
                        {groupHeader}
                        <Button
                            id={`${uid}_${groupKey}_toggleAllColumns`}
                            onClick={this.onToggleAllColumnsButtonClick.bind( this, groupKey )}

                            label={__( 'YN.base.columnConfig.toggleAllColumns' )}
                            className="p-button-link yn-link-button"
                        />
                        <hr className="mt-1 yn-ruler"/>
                        <div className="yn-selectiongroup-columns">
                            {groupColumns}
                        </div>
                    </div>
                );

                columnGroupCount++;
            }
        }
        else {
            const columns                       = this.state.columns as unknown as ColumnConfigMenuSingleColumn[],
                  groupColumns : ReactElement[] = [];

            // group columns
            if ( !noSort ) {
                columns.sort( ( column1, column2 ) => {
                    const sortLabel1 = ( column1.header ?? column1.field ?? '' ).toLowerCase(),
                          sortLabel2 = ( column2.header ?? column2.field ?? '' ).toLowerCase();

                    if ( sortLabel1 > sortLabel2 ) {
                        return 1;
                    }
                    else if ( sortLabel1 < sortLabel2 ) {
                        return -1;
                    }
                    else {
                        return 0;
                    }
                } );
            }
            for ( const column of columns ) {
                if ( !column.hidden || column.hidable ) {
                    // colons must not be part of HTML element id as we can't query them
                    const key = `${uid}_${column.field}`.replace( /:/g, '_' );

                    groupColumns.push(
                        <div key={key} className="yn-selectiongroup-column">
                            <InputSwitch
                                inputId={`${key}_switch`}
                                checked={!column.hidden}
                                disabled={!column.hidable}
                                onChange={this.onColumnVisibilityChange.bind( this, '', column.field )}
                            />
                            <label htmlFor={`${key}_switch`}>
                                {column.header ?? column.field}
                            </label>
                        </div>
                    );
                }
            }

            groups.push(
                <div key={`${uid}_noGroup`} className="yn-selectiongroup" style={{ width: '100%' }}>
                    <Button
                        id={`${uid}_toggleAllColumns`}
                        onClick={this.onToggleAllColumnsButtonClick.bind( this, '' )}

                        label={__( 'YN.base.columnConfig.toggleAllColumns' )}
                        className="p-button-link yn-link-button"
                    />
                    <hr className="yn-ruler"/>
                    <div className="yn-selectiongroup-columns">
                        {groupColumns}
                    </div>
                </div>
            );
        }

        return <div className="flex" style={{ flex: 1, overflow: 'hidden', maxHeight: '50vh' }}>{groups}</div>;
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  uid,
                  id,
                  className,
                  disabled,
                  tabIndex
              } = this.props;

        return <ContentButton
            id={id}

            ref={element => this.contentButton = element}

            horizontalContentAlign="right"
            verticalContentAlign="bottom"
            rounded={true}
            text={true}
            icon="pi pi-table"
            tooltip={__( 'YN.base.columnConfig.button' )}
            tooltipOptions={config.frontend?.buttonTooltipOptions}
            className={`${className} yn-option-button yn-column-option-button`}
            contentClassName="yn-contentbutton-overlay-content-page-menu"
            tabIndex={tabIndex}

            disabled={disabled}

            onCollapse={this.onContentButtonCollapse}

            content={
                <Panel
                    className="yn-flex yn-finder-column-options"
                    style={{
                        width: '75rem'
                    }}
                    pt={{
                        content: {
                            style: {
                                border: 'none'
                            }
                        }
                    }}
                >
                    <label
                        key={`${uid}_header`}

                        className="yn-menu-title-label"
                    >
                        {__( 'YN.base.columnConfig.label' )}
                    </label>

                    {this.renderColumnOptions()}

                    <hr className="mt-3 yn-ruler"/>

                    {this.renderColumnGroups()}

                    <hr className="mt-3 mb-3 yn-ruler"/>

                    <div className="flex justify-content-end">
                        <Button
                            label={__( 'YN.base.common.apply' )}
                            onClick={this.onApplyButtonClick}
                            className="mr-3 p-button-outlined yn-button-defaultwidth text-center"
                        />
                        <Button
                            label={__( 'YN.base.common.cancel' )}
                            onClick={this.onCancelButtonClick}
                            className="p-button-secondary p-button-outlined yn-button-defaultwidth text-center"
                        />
                    </div>
                </Panel>
            }
        />;
    }

}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        user: undefined
    };

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

export default connect( mapStateToProps, null )( ColumnConfigMenu );
